import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { formatPrice, formatDate } from '@/utils/format';
import {
  usePurchaseOrders,
  usePurchaseOrder,
  useCreatePurchaseOrder,
  useCancelPurchaseOrder,
  useDeletePurchaseOrder,
  useUpdatePurchaseOrder,
  useReceivePurchaseOrder,
  useRegisterSupplierPayment,
  useDeleteSupplierPayment,
  useSupplierPayments,
  useSupplierDebtSummary,
} from '@/hooks/usePurchaseOrders';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useProducts } from '@/hooks/useProducts';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { productsService } from '@/services/products';
import toast from 'react-hot-toast';
import {
  PurchaseOrderCreate,
  PurchaseOrderItemCreate,
  ReceiveItemData,
  SupplierPaymentCreate,
  SupplierMovementType,
  SUPPLIER_MOVEMENT_LABELS,
} from '@/services/purchaseOrders';
import { PurchaseOrder } from '@/types';
import {
  Plus,
  Eye,
  XCircle,
  Trash2,
  Package,
  DollarSign,
  ClipboardList,
  AlertCircle,
  Pencil,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Label maps
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  confirmed: 'Confirmada',
  partial_received: 'Recibida Parcial',
  received: 'Recibida',
  cancelled: 'Cancelada',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  transfer: 'Transferencia',
  card: 'Tarjeta',
  check: 'Cheque',
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  standard: 'Orden',
  debt_only: 'Deuda',
};

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'confirmed':
      return 'bg-blue-100 text-blue-800';
    case 'partial_received':
      return 'bg-yellow-100 text-yellow-800';
    case 'received':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// ---------------------------------------------------------------------------
// Safe string helper (React #310 prevention)
// ---------------------------------------------------------------------------

function s(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function n(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PurchaseOrders() {
  const { user } = useAuthStore();
  const canWrite = user?.effective_permissions?.includes('purchases') ?? false;

  // Filters
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailPOId, setDetailPOId] = useState<number | null>(null);
  const [cancelConfirmPO, setCancelConfirmPO] = useState<PurchaseOrder | null>(null);
  const [deleteConfirmPO, setDeleteConfirmPO] = useState<PurchaseOrder | null>(null);
  const [payingSupplierId, setPayingSupplierId] = useState<number | null>(null);
  const [payingPendingAmount, setPayingPendingAmount] = useState(0);

  // Queries
  const filters = {
    supplier_id: filterSupplier ? Number(filterSupplier) : undefined,
    status: filterStatus || undefined,
  };
  const { data: orders, isLoading } = usePurchaseOrders(filters);
  const { data: debtSummary } = useSupplierDebtSummary();
  const { data: suppliers } = useSuppliers();
  const cancelPO = useCancelPurchaseOrder();
  const deletePO = useDeletePurchaseOrder();

  // Supplier options for filters
  const supplierFilterOptions = [
    { value: '', label: 'Todos los proveedores' },
    ...(suppliers?.map((sup) => ({ value: sup.id.toString(), label: sup.name })) ?? []),
  ];

  const statusFilterOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'draft', label: 'Borrador' },
    { value: 'confirmed', label: 'Confirmada' },
    { value: 'partial_received', label: 'Recibida Parcial' },
    { value: 'received', label: 'Recibida' },
    { value: 'cancelled', label: 'Cancelada' },
  ];

  const handleCancelPO = async () => {
    if (!cancelConfirmPO) return;
    try {
      await cancelPO.mutateAsync(cancelConfirmPO.id);
      setCancelConfirmPO(null);
    } catch (error) {
      console.error('Error cancelling PO:', error);
    }
  };

  const handleDeletePO = async () => {
    if (!deleteConfirmPO) return;
    try {
      await deletePO.mutateAsync(deleteConfirmPO.id);
      setDeleteConfirmPO(null);
      setDetailPOId(null);
    } catch (error) {
      console.error('Error deleting PO:', error);
    }
  };

  // Debt summary: all suppliers with orders (pending first, then settled)
  const allDebts = debtSummary ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ordenes de Compra</h1>
          <p className="text-gray-600">Gestiona compras, deudas y pagos a proveedores</p>
        </div>
        {canWrite ? (
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Orden de Compra
          </Button>
        ) : null}
      </div>

      {/* Debt summary cards */}
      {allDebts.length > 0 ? (
        /*
          Tarjetas en vertical: antes el icono, el texto y los botones iban en una sola
          fila y con 4 columnas cada tarjeta quedaba en ~280px. El nombre del proveedor
          se cortaba ("Baza...") y la linea de totales se encimaba con los botones.
          Tambien se baja a 3 columnas: la cuarta solo entra en pantallas muy anchas.
        */
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {allDebts.map((debt) => {
            // Negativo = saldo A FAVOR de la veterinaria (por una nota de credito que
            // supero la deuda). Antes cualquier valor <= 0 se mostraba como "Saldado".
            const pending = n(debt.pending_amount);
            const hasCredit = pending < 0;
            const isSettled = pending === 0;
            const cantidad = n(debt.purchase_order_count);
            return (
              <div
                key={debt.supplier_id}
                className={`bg-white rounded-lg shadow p-4 flex flex-col gap-3 ${
                  isSettled ? 'opacity-75' : ''
                }`}
              >
                {/* Proveedor */}
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                    hasCredit ? 'bg-blue-100' : isSettled ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <span className={`text-lg font-bold ${
                      hasCredit ? 'text-blue-600' : isSettled ? 'text-green-600' : 'text-red-600'
                    }`}>{hasCredit ? '+' : isSettled ? '✓' : '!'}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* El nombre completo, en dos lineas si hace falta */}
                    <p
                      className="text-sm font-medium text-gray-900 break-words leading-snug"
                      title={s(debt.supplier_name)}
                    >
                      {s(debt.supplier_name)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {s(debt.purchase_order_count)} {cantidad === 1 ? 'orden' : 'órdenes'}
                    </p>
                  </div>
                </div>

                {/* Saldo */}
                <div>
                  {hasCredit ? (
                    <>
                      <p className="text-xl font-bold text-blue-600 leading-tight">
                        {formatPrice(-pending)}
                      </p>
                      <p className="text-xs font-medium text-blue-600">a favor tuyo</p>
                    </>
                  ) : isSettled ? (
                    <p className="text-xl font-bold text-green-600 leading-tight">Saldado</p>
                  ) : (
                    <p className="text-xl font-bold text-red-600 leading-tight">
                      {formatPrice(debt.pending_amount)}
                    </p>
                  )}
                </div>

                {/* Detalle: etiqueta a la izquierda, importe a la derecha */}
                <div className="text-xs text-gray-500 space-y-0.5 border-t border-gray-100 pt-2">
                  <div className="flex justify-between gap-2">
                    <span>Total</span>
                    <span className="font-medium text-gray-700">{formatPrice(debt.total_debt)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Pagado</span>
                    <span className="font-medium text-gray-700">{formatPrice(debt.total_paid)}</span>
                  </div>
                </div>

                {/* Acciones, a lo ancho de la tarjeta */}
                {canWrite ? (
                  <div className="flex gap-2 mt-auto">
                    <Button
                      size="sm"
                      variant={isSettled || hasCredit ? 'secondary' : 'primary'}
                      className="flex-1"
                      onClick={() => {
                        setPayingSupplierId(debt.supplier_id);
                        setPayingPendingAmount(n(debt.pending_amount));
                      }}
                    >
                      {isSettled || hasCredit ? 'Ver movimientos' : 'Pagar'}
                    </Button>
                    {/* Con deuda pendiente antes solo se podia entrar por "Pagar", asi
                        que para mirar que hay adentro habia que hacer como si se fuera
                        a pagar. Se abre el mismo detalle, sin esa sensacion. */}
                    {!isSettled && !hasCredit && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="flex-1"
                        onClick={() => {
                          setPayingSupplierId(debt.supplier_id);
                          setPayingPendingAmount(n(debt.pending_amount));
                        }}
                      >
                        Ver movimientos
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            options={supplierFilterOptions}
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            label="Proveedor"
          />
          <Select
            options={statusFilterOptions}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Estado"
          />
        </div>
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Cargando...</div>
        ) : orders && orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    OC #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proveedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((po) => {
                  const canCancel =
                    po.status !== 'cancelled' &&
                    po.status !== 'received';

                  return (
                    <tr key={po.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {s(po.po_number)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {s(po.supplier_name)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {ORDER_TYPE_LABELS[po.order_type] ? s(ORDER_TYPE_LABELS[po.order_type]) : s(po.order_type)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                        {formatPrice(po.total_amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(po.status)}`}
                        >
                          {STATUS_LABELS[po.status] ? s(STATUS_LABELS[po.status]) : s(po.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(po.order_date || po.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailPOId(po.id)}
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canWrite && canCancel ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCancelConfirmPO(po)}
                              title="Cancelar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canWrite ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirmPO(po)}
                              title="Eliminar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            No hay ordenes de compra
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Nueva Orden de Compra"
        size="2xl"
      >
        <CreatePurchaseOrderForm
          onSuccess={() => setCreateModalOpen(false)}
        />
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={detailPOId !== null}
        onClose={() => setDetailPOId(null)}
        title="Detalle de Orden de Compra"
        size="xl"
      >
        {detailPOId !== null ? (
          <PurchaseOrderDetail
            poId={detailPOId}
            canWrite={canWrite}
            onClose={() => setDetailPOId(null)}
          />
        ) : null}
      </Modal>

      {/* Cancel confirmation */}
      <Modal
        isOpen={cancelConfirmPO !== null}
        onClose={() => setCancelConfirmPO(null)}
        title="Cancelar Orden de Compra"
        size="sm"
      >
        {cancelConfirmPO !== null ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-red-50 text-red-800 p-3 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                Esta accion no se puede deshacer. Se cancelara la orden{' '}
                <strong>{s(cancelConfirmPO.po_number)}</strong>.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCancelConfirmPO(null)}>
                Volver
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelPO}
                loading={cancelPO.isPending}
              >
                Cancelar Orden
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={deleteConfirmPO !== null}
        onClose={() => setDeleteConfirmPO(null)}
        title="Eliminar Orden de Compra"
        size="sm"
      >
        {deleteConfirmPO !== null ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-red-50 text-red-800 p-3 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                Se eliminara permanentemente la orden{' '}
                <strong>{s(deleteConfirmPO.po_number)}</strong> y todos sus datos asociados.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirmPO(null)}>
                Volver
              </Button>
              <Button
                variant="danger"
                onClick={handleDeletePO}
                loading={deletePO.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Supplier Payment Modal */}
      <Modal
        isOpen={payingSupplierId !== null}
        onClose={() => setPayingSupplierId(null)}
        title="Pago a Proveedor"
        size="md"
      >
        {payingSupplierId !== null ? (
          <SupplierPaymentForm
            supplierId={payingSupplierId}
            pendingAmount={payingPendingAmount}
            onSuccess={() => setPayingSupplierId(null)}
          />
        ) : null}
      </Modal>
    </div>
  );
}

// ===========================================================================
// Create Purchase Order Form
// ===========================================================================

function CreatePurchaseOrderForm({ onSuccess }: { onSuccess: () => void }) {
  const [orderType, setOrderType] = useState<'standard' | 'debt_only'>('standard');
  const [supplierId, setSupplierId] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [discountPct, setDiscountPct] = useState('0');
  const [taxPct, setTaxPct] = useState('21');
  const [extraTaxLabel, setExtraTaxLabel] = useState('');
  const [extraTaxPct, setExtraTaxPct] = useState('0');

  // Debt-only fields
  const [debtAmount, setDebtAmount] = useState('');
  const [debtAmountFormatted, setDebtAmountFormatted] = useState(false);
  const [debtDescription, setDebtDescription] = useState('');

  // Standard items
  const [items, setItems] = useState<
    {
      mode: 'existing' | 'custom';
      product_id: string;
      custom_name: string;
      quantity: string;
      unit_cost: string;
      discount: string;
    }[]
  >([]);

  const { data: suppliers } = useSuppliers();
  const { data: products } = useProducts();
  const [productSearches, setProductSearches] = useState<Record<number, string>>({});
  const [productSearchDebounced, setProductSearchDebounced] = useState('');
  const [openProductDropdown, setOpenProductDropdown] = useState<number | null>(null);
  const [selectedProductNames, setSelectedProductNames] = useState<Record<number, string>>({});

  // Debounce de búsqueda
  const currentSearch = openProductDropdown !== null ? (productSearches[openProductDropdown] ?? '') : '';
  useEffect(() => {
    const timer = setTimeout(() => setProductSearchDebounced(currentSearch), 250);
    return () => clearTimeout(timer);
  }, [currentSearch]);

  // Query dinámica: siempre activa cuando dropdown abierto
  const { data: searchResults } = useProducts(
    { search: productSearchDebounced || undefined, limit: 50 },
    { enabled: openProductDropdown !== null }
  );

  useEffect(() => {
    if (openProductDropdown === null) return;
    const handleClick = () => setOpenProductDropdown(null);
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handleClick); };
  }, [openProductDropdown]);
  const createPO = useCreatePurchaseOrder();

  const supplierOptions = [
    { value: '', label: 'Seleccionar proveedor...' },
    ...(suppliers?.map((sup) => ({ value: sup.id.toString(), label: sup.name })) ?? []),
  ];


  const addItem = () => {
    setItems([
      ...items,
      { mode: 'existing', product_id: '', custom_name: '', quantity: '1', unit_cost: '', discount: '0' },
    ]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: string, value: string) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-fill unit cost when selecting an existing product
    if (field === 'product_id' && value) {
      const allProducts = [...(products || []), ...(searchResults || [])];
      const product = allProducts.find((p) => p.id === Number(value));
      if (product && product.cost_price) {
        updated[index].unit_cost = String(n(product.cost_price));
      }
    }

    setItems(updated);
  };

  // Lector de código de barras: agrega el producto a la orden (o suma cantidad)
  const handleBarcodeScan = async (code: string) => {
    let product = (products || []).find((p) => p.barcode === code) || null;
    if (!product) {
      try {
        product = await productsService.getProductByBarcode(code);
      } catch {
        toast.error('Error al buscar el código de barras');
        return;
      }
    }
    if (!product) {
      toast.error(`No hay un producto con el código ${code}`);
      return;
    }
    const found = product;
    const existingIdx = items.findIndex(
      (it) => it.mode === 'existing' && it.product_id === String(found.id)
    );
    if (existingIdx !== -1) {
      const copy = [...items];
      copy[existingIdx] = {
        ...copy[existingIdx],
        quantity: String((parseFloat(copy[existingIdx].quantity) || 0) + 1),
      };
      setItems(copy);
    } else {
      const newIndex = items.length;
      setItems([
        ...items,
        {
          mode: 'existing',
          product_id: String(found.id),
          custom_name: '',
          quantity: '1',
          unit_cost: found.cost_price ? String(n(found.cost_price)) : '',
          discount: '0',
        },
      ]);
      setSelectedProductNames({ ...selectedProductNames, [newIndex]: found.name });
    }
    toast.success(`${found.name} agregado`);
  };

  // Activo mientras el formulario de orden de compra está montado
  useBarcodeScanner(handleBarcodeScan, { enabled: openProductDropdown === null });

  const itemsTotal = items.reduce((acc, item) => {
    return acc + n(item.quantity) * n(item.unit_cost);
  }, 0);

  // Subtotal con descuentos por item (para deuda)
  const itemsTotalWithDiscounts = items.reduce((acc, item) => {
    const lineTotal = n(item.quantity) * n(item.unit_cost);
    const itemDiscount = n(item.discount) / 100;
    return acc + lineTotal * (1 - itemDiscount);
  }, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: PurchaseOrderCreate = {
      supplier_id: supplierId ? Number(supplierId) : undefined,
      order_type: orderType,
      order_date: orderDate || undefined,
      notes: notes || undefined,
      discount_percentage: n(discountPct) || 0,
      tax_percentage: n(taxPct) || 0,
      extra_tax_label: extraTaxLabel.trim() || null,
      extra_tax_percentage: n(extraTaxPct) || 0,
      items: [],
    };

    if (orderType === 'debt_only') {
      payload.total_amount = n(debtAmount);
      payload.description = debtDescription || undefined;
    } else {
      payload.items = items.map((item): PurchaseOrderItemCreate => {
        const base = {
          quantity_ordered: n(item.quantity),
          unit_cost: n(item.unit_cost),
          discount_percentage: n(item.discount) || 0,
        };
        if (item.mode === 'existing' && item.product_id) {
          return { ...base, product_id: Number(item.product_id) };
        }
        return { ...base, custom_name: item.custom_name || 'Producto sin nombre' };
      });
    }

    try {
      await createPO.mutateAsync(payload);
      onSuccess();
    } catch (error) {
      console.error('Error creating PO:', error);
    }
  };

  const isValid =
    orderType === 'debt_only'
      ? !!supplierId && n(debtAmount) > 0
      : !!supplierId && items.length > 0 && items.every((item) => {
          const hasProduct = item.mode === 'existing' ? !!item.product_id : !!item.custom_name;
          return hasProduct && n(item.quantity) > 0 && n(item.unit_cost) > 0;
        });

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Order type toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de orden</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOrderType('standard')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
              orderType === 'standard'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Orden completa
          </button>
          <button
            type="button"
            onClick={() => setOrderType('debt_only')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
              orderType === 'debt_only'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <DollarSign className="h-4 w-4 inline mr-2" />
            Solo registrar deuda
          </button>
        </div>
      </div>

      {/* Supplier */}
      <Select
        label="Proveedor *"
        options={supplierOptions}
        value={supplierId}
        onChange={(e) => setSupplierId(e.target.value)}
      />

      {/* Order date */}
      <Input
        type="date"
        label="Fecha de la compra"
        value={orderDate}
        onChange={(e) => setOrderDate(e.target.value)}
      />

      {/* Debt-only mode */}
      {orderType === 'debt_only' ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto total *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={debtAmountFormatted && n(debtAmount) > 0
                  ? n(debtAmount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : debtAmount}
                onChange={(e) => {
                  setDebtAmountFormatted(false);
                  setDebtAmount(e.target.value.replace(/[^\d.,]/g, ''));
                }}
                onFocus={() => {
                  setDebtAmountFormatted(false);
                  if (n(debtAmount) > 0) setDebtAmount(String(n(debtAmount)));
                }}
                onBlur={() => {
                  let val = debtAmount.replace(/\./g, '').replace(',', '.');
                  const num = parseFloat(val);
                  if (!isNaN(num) && num > 0) {
                    setDebtAmount(String(num));
                    setDebtAmountFormatted(true);
                  }
                }}
                placeholder="0,00"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
            <textarea
              value={debtDescription}
              onChange={(e) => setDebtDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
              placeholder="Descripcion de la deuda..."
            />
          </div>
        </div>
      ) : (
        /* Standard mode - items table */
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">Items</label>
            <Button type="button" size="sm" variant="secondary" onClick={addItem}>
              <Plus className="h-3 w-3 mr-1" />
              Agregar item
            </Button>
          </div>

          {items.length > 0 ? (
            <div className="border rounded-lg overflow-visible">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Producto
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">
                      Cant.
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">
                      Costo unit.
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">
                      Dto %
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">
                      Subtotal
                    </th>
                    <th className="px-3 py-2 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, idx) => {
                    const lineTotal = n(item.quantity) * n(item.unit_cost);
                    return (
                      <tr key={idx}>
                        <td className="px-3 py-2">
                          <div className="space-y-1">
                            <div className="flex gap-2 items-center">
                              <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`item-mode-${String(idx)}`}
                                  checked={item.mode === 'existing'}
                                  onChange={() => updateItem(idx, 'mode', 'existing')}
                                  className="text-primary-600"
                                />
                                Existente
                              </label>
                              <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`item-mode-${String(idx)}`}
                                  checked={item.mode === 'custom'}
                                  onChange={() => updateItem(idx, 'mode', 'custom')}
                                  className="text-primary-600"
                                />
                                Nuevo
                              </label>
                            </div>
                            {item.mode === 'existing' ? (
                              <ProductSearchSelect
                                value={selectedProductNames[idx] || ''}
                                searchValue={openProductDropdown === idx ? (productSearches[idx] ?? '') : undefined}
                                isOpen={openProductDropdown === idx}
                                results={openProductDropdown === idx ? (searchResults || []) : []}
                                isSearching={!!currentSearch}
                                onFocus={() => {
                                  setOpenProductDropdown(idx);
                                  setProductSearches({ ...productSearches, [idx]: '' });
                                }}
                                onSearch={(val) => {
                                  setProductSearches({ ...productSearches, [idx]: val });
                                  setOpenProductDropdown(idx);
                                }}
                                onSelect={(p) => {
                                  updateItem(idx, 'product_id', p.id.toString());
                                  setSelectedProductNames({ ...selectedProductNames, [idx]: p.name });
                                  setOpenProductDropdown(null);
                                  setProductSearches({ ...productSearches, [idx]: '' });
                                }}
                              />
                            ) : (
                              <input
                                type="text"
                                value={item.custom_name}
                                onChange={(e) => updateItem(idx, 'custom_name', e.target.value)}
                                placeholder="Nombre del producto"
                                className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                            min="0"
                            step="any"
                            className="w-full text-sm text-right px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.unit_cost}
                            onChange={(e) => updateItem(idx, 'unit_cost', e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="w-full text-sm text-right px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.discount}
                            onChange={(e) => updateItem(idx, 'discount', e.target.value)}
                            min="0"
                            max="100"
                            step="0.5"
                            placeholder="0"
                            className="w-full text-sm text-right px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-sm font-medium">
                          <div>{formatPrice(lineTotal)}</div>
                          {n(item.discount) > 0 && (
                            <div className="text-xs text-red-500 mt-0.5">
                              -{n(item.discount)}%: {formatPrice(lineTotal * (1 - n(item.discount) / 100))}
                            </div>
                          )}
                          {n(taxPct) > 0 && n(item.unit_cost) > 0 && (
                            <div className="text-xs text-green-600 mt-0.5">
                              c/IVA: {formatPrice(n(item.unit_cost) * (1 + n(taxPct) / 100))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500 text-sm">
              No hay items. Haz clic en "Agregar item" para comenzar.
            </div>
          )}

          {/* Resumen con descuento + IVA */}
          {items.length > 0 && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Resumen de la orden</h4>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Descuento del proveedor (%)</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={discountPct}
                      onChange={(e) => setDiscountPct(e.target.value)}
                      min="0"
                      max="100"
                      step="0.5"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">IVA (%)</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={taxPct}
                      onChange={(e) => setTaxPct(e.target.value)}
                      min="0"
                      max="100"
                      step="0.5"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                </div>
              </div>
              {/* Impuesto adicional opcional */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Otro impuesto (opcional)</label>
                  <input
                    type="text"
                    value={extraTaxLabel}
                    onChange={(e) => setExtraTaxLabel(e.target.value)}
                    placeholder="Ej: IIBB"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">% Otro impuesto</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={extraTaxPct}
                      onChange={(e) => setExtraTaxPct(e.target.value)}
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                </div>
              </div>
              {(() => {
                const hasItemDiscounts = items.some(i => n(i.discount) > 0);
                const subtotalAfterItemDiscounts = itemsTotalWithDiscounts;
                const discountAmount = subtotalAfterItemDiscounts * (n(discountPct) / 100);
                const net = subtotalAfterItemDiscounts - discountAmount;
                const taxAmount = net * (n(taxPct) / 100);
                const extraTaxAmount = net * (n(extraTaxPct) / 100);
                const total = net + taxAmount + extraTaxAmount;
                return (
                  <div className="space-y-1.5 text-sm border-t border-gray-200 pt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Deuda al proveedor</p>
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal productos</span>
                      <span>{formatPrice(itemsTotal)}</span>
                    </div>
                    {hasItemDiscounts && (
                      <div className="flex justify-between text-red-500">
                        <span>Descuentos por producto</span>
                        <span>-{formatPrice(itemsTotal - subtotalAfterItemDiscounts)}</span>
                      </div>
                    )}
                    {n(discountPct) > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Descuento general ({discountPct}%)</span>
                        <span>-{formatPrice(discountAmount)}</span>
                      </div>
                    )}
                    {(hasItemDiscounts || n(discountPct) > 0) && (
                      <div className="flex justify-between text-gray-600">
                        <span>Neto</span>
                        <span>{formatPrice(net)}</span>
                      </div>
                    )}
                    {n(taxPct) > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>IVA ({taxPct}%)</span>
                        <span>+{formatPrice(taxAmount)}</span>
                      </div>
                    )}
                    {n(extraTaxPct) > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>{extraTaxLabel.trim() || 'Otro impuesto'} ({extraTaxPct}%)</span>
                        <span>+{formatPrice(extraTaxAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-gray-900 border-t-2 border-gray-300 pt-2 mt-2 text-base">
                      <span>Total deuda</span>
                      <span>{formatPrice(total)}</span>
                    </div>
                    {n(discountPct) > 0 && n(taxPct) > 0 && (
                      <p className="text-xs text-gray-400 italic mt-1">
                        El descuento solo aplica a la deuda, no al costo del producto.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          placeholder="Notas adicionales..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" loading={createPO.isPending} disabled={!isValid}>
          Crear Orden
        </Button>
      </div>
    </form>
  );
}

// ===========================================================================
// Purchase Order Detail
// ===========================================================================

function PurchaseOrderDetail({
  poId,
  canWrite,
}: {
  poId: number;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { data: po, isLoading } = usePurchaseOrder(poId);
  const updatePO = useUpdatePurchaseOrder();
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editDate, setEditDate] = useState('');

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Cargando...</div>;
  }

  if (!po) {
    return <div className="text-center py-8 text-gray-500">Orden no encontrada</div>;
  }

  const canReceive =
    po.status !== 'received' &&
    po.status !== 'cancelled' &&
    po.order_type === 'standard';

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase">Orden</p>
            <p className="text-sm font-bold text-gray-900">{s(po.po_number)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Proveedor</p>
            <p className="text-sm font-medium text-gray-900">{s(po.supplier_name)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Fecha</p>
            {editingDate ? (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  className="text-sm border rounded px-1 py-0.5"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
                <button
                  className="text-green-600 text-xs font-medium"
                  onClick={async () => {
                    if (editDate) {
                      await updatePO.mutateAsync({ id: po.id, data: { order_date: editDate } });
                    }
                    setEditingDate(false);
                  }}
                >
                  OK
                </button>
                <button
                  className="text-gray-400 text-xs"
                  onClick={() => setEditingDate(false)}
                >
                  X
                </button>
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                {formatDate(po.order_date || po.created_at)}
                {canWrite ? (
                  <button
                    onClick={() => {
                      const d = po.order_date || po.created_at;
                      setEditDate(d ? d.split('T')[0] : '');
                      setEditingDate(true);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                ) : null}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Estado</p>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(po.status)}`}
            >
              {STATUS_LABELS[po.status] ? s(STATUS_LABELS[po.status]) : s(po.status)}
            </span>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Total deuda</p>
            <p className="text-lg font-bold text-gray-900">{formatPrice(po.total_amount)}</p>
            {(n(po.discount_percentage) > 0 || n(po.tax_percentage) > 0 || n(po.extra_tax_percentage) > 0) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {n(po.discount_percentage) > 0 && <>Desc: {po.discount_percentage}% </>}
                {n(po.tax_percentage) > 0 && <>· IVA: {po.tax_percentage}% </>}
                {n(po.extra_tax_percentage) > 0 && <>· {po.extra_tax_label || 'Otro'}: {po.extra_tax_percentage}%</>}
              </p>
            )}
          </div>
        </div>

        {po.description ? (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 uppercase">Descripcion</p>
            <p className="text-sm text-gray-700">{s(po.description)}</p>
          </div>
        ) : null}

        {po.notes ? (
          <div className="mt-2">
            <p className="text-xs text-gray-500 uppercase">Notas</p>
            <p className="text-sm text-gray-700">{s(po.notes)}</p>
          </div>
        ) : null}
      </div>

      {/* Items section */}
      {po.order_type === 'standard' && po.items && po.items.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 uppercase">Items</h4>
            {canWrite && canReceive ? (
              <Button size="sm" onClick={() => setReceiveModalOpen(true)}>
                <Package className="h-4 w-4 mr-1" />
                Recibir Mercaderia
              </Button>
            ) : null}
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pedido
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recibido
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pendiente
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costo unit.
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {po.items.map((item) => {
                  const ordered = n(item.quantity_ordered);
                  const received = n(item.quantity_received);
                  const remaining = ordered - received;
                  const itemName = item.product_name ? s(item.product_name) : item.custom_name ? s(item.custom_name) : '-';

                  return (
                    <tr key={item.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {itemName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        {s(ordered)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-700">
                        {s(received)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        {remaining > 0 ? (
                          <span className="text-orange-600 font-medium">{s(remaining)}</span>
                        ) : (
                          <span className="text-green-600">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        {formatPrice(item.unit_cost)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                        {formatPrice(item.line_total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Receive modal */}
      <Modal
        isOpen={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        title="Recibir Mercaderia"
        size="2xl"
      >
        <ReceiveItemsForm
          po={po}
          onSuccess={() => setReceiveModalOpen(false)}
        />
      </Modal>

    </div>
  );
}

// ===========================================================================
// Receive Items Form
// ===========================================================================

function ReceiveItemsForm({
  po,
  onSuccess,
}: {
  po: PurchaseOrder;
  onSuccess: () => void;
}) {
  // Only items with pending quantities
  const pendingItems = (po.items ?? []).filter(
    (item) => n(item.quantity_ordered) - n(item.quantity_received) > 0
  );

  const [quantities, setQuantities] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    pendingItems.forEach((item) => {
      const remaining = n(item.quantity_ordered) - n(item.quantity_received);
      initial[item.id] = String(remaining);
    });
    return initial;
  });
  const [receiveNotes, setReceiveNotes] = useState('');

  const receivePO = useReceivePurchaseOrder();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const receiveItems: ReceiveItemData[] = pendingItems
      .filter((item) => n(quantities[item.id]) > 0)
      .map((item) => ({
        item_id: item.id,
        quantity_received: n(quantities[item.id]),
      }));

    if (receiveItems.length === 0) return;

    try {
      await receivePO.mutateAsync({
        poId: po.id,
        data: {
          items: receiveItems,
          notes: receiveNotes || undefined,
        },
      });
      onSuccess();
    } catch (error) {
      console.error('Error receiving items:', error);
    }
  };

  if (pendingItems.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        Todos los items ya fueron recibidos.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Producto
              </th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                Pedido
              </th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                Recibido
              </th>
              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Pendiente
              </th>
              <th className="px-2 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50 w-28">
                Recibir ahora
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pendingItems.map((item) => {
              const ordered = n(item.quantity_ordered);
              const received = n(item.quantity_received);
              const remaining = ordered - received;
              const itemName = item.product_name
                ? s(item.product_name)
                : item.custom_name
                  ? s(item.custom_name)
                  : '-';

              return (
                <tr key={item.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {itemName}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                    {s(ordered)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-700">
                    {s(received)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-orange-600 font-medium">
                    {s(remaining)}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap bg-blue-50">
                    <input
                      type="number"
                      value={quantities[item.id] ?? ''}
                      onChange={(e) =>
                        setQuantities({ ...quantities, [item.id]: e.target.value })
                      }
                      min="0"
                      max={remaining}
                      step="any"
                      className="w-full text-sm text-right px-2 py-1.5 border-2 border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
        <textarea
          value={receiveNotes}
          onChange={(e) => setReceiveNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          placeholder="Notas de recepcion..."
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" loading={receivePO.isPending}>
          Confirmar Recepcion
        </Button>
      </div>
    </form>
  );
}

// ===========================================================================
// Supplier Payment Form (pays against the supplier account, not a single PO)
// ===========================================================================

function SupplierPaymentForm({
  supplierId,
  pendingAmount,
  onSuccess,
}: {
  supplierId: number;
  pendingAmount: number;
  onSuccess: () => void;
}) {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [amount, setAmount] = useState('');
  const [amountFormatted, setAmountFormatted] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(null);
  // Que se esta registrando: un pago, una nota de credito o una de debito
  const [movementType, setMovementType] = useState<SupplierMovementType>('payment');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [deductFromRegister, setDeductFromRegister] = useState(true);
  const [reference, setReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  const registerPayment = useRegisterSupplierPayment();
  const deletePayment = useDeleteSupplierPayment();
  const { data: payments } = useSupplierPayments(supplierId);

  const paymentMethodOptions = [
    { value: 'cash', label: 'Efectivo' },
    { value: 'transfer', label: 'Transferencia' },
    { value: 'card', label: 'Tarjeta' },
    { value: 'check', label: 'Cheque' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = n(amount);
    if (parsedAmount <= 0) return;
    // Solo los PAGOS estan limitados por la deuda. Una nota de credito puede superarla y
    // dejar saldo a favor, que es justamente para lo que se usa.
    if (movementType === 'payment' && pendingAmount > 0 && parsedAmount > pendingAmount) return;

    // First click: show confirmation
    if (!confirmingPayment) {
      setConfirmingPayment(true);
      return;
    }

    // Second click: actually submit
    const payload: SupplierPaymentCreate = {
      amount: parsedAmount,
      movement_type: movementType,
      payment_method: paymentMethod,
      payment_date: paymentDate || undefined,
      reference: reference || undefined,
      notes: paymentNotes || undefined,
      deduct_from_register: deductFromRegister,
    };

    try {
      await registerPayment.mutateAsync({ supplierId, data: payload });
      onSuccess();
    } catch (error) {
      console.error('Error registering payment:', error);
    }
  };

  return (
    <div className="space-y-5">
      {/* Balance info */}
      <div className={`${pendingAmount > 0 ? 'bg-red-50' : 'bg-green-50'} p-4 rounded-lg text-center`}>
        <p className={`text-xs ${pendingAmount > 0 ? 'text-red-600' : 'text-green-600'} uppercase`}>
          {pendingAmount > 0 ? 'Saldo pendiente' : 'Cuenta saldada'}
        </p>
        <p className={`text-2xl font-bold ${pendingAmount > 0 ? 'text-red-700' : 'text-green-700'}`}>
          {formatPrice(pendingAmount)}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Que se registra: pago, nota de credito o nota de debito */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de movimiento</label>
          <div className="grid grid-cols-3 gap-2">
            {(['payment', 'credit_note', 'debit_note'] as SupplierMovementType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setMovementType(t); setConfirmingPayment(false); }}
                className={`px-2 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                  movementType === t
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {SUPPLIER_MOVEMENT_LABELS[t]}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {movementType === 'payment'
              ? 'Baja la deuda y sale plata de la caja.'
              : movementType === 'credit_note'
                ? 'Baja la deuda sin mover plata. Si supera lo que debés, queda saldo a favor.'
                : 'Suma a la deuda: intereses, flete, diferencias de precio.'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {movementType === 'payment' ? 'Monto a pagar *' : 'Monto de la nota *'}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={amountFormatted && n(amount) > 0
                ? n(amount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : amount}
              onChange={(e) => {
                setAmountFormatted(false);
                setConfirmingPayment(false);
                // Allow digits, dot, comma
                const val = e.target.value.replace(/[^\d.,]/g, '');
                setAmount(val);
              }}
              onFocus={() => {
                setAmountFormatted(false);
                // Show raw number for easy editing
                if (n(amount) > 0) setAmount(String(n(amount)));
              }}
              onBlur={() => {
                // Parse and clean the value
                let val = amount.replace(/\./g, '').replace(',', '.');
                const num = parseFloat(val);
                if (!isNaN(num) && num > 0) {
                  setAmount(String(num));
                  setAmountFormatted(true);
                }
              }}
              placeholder="0,00"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
            />
          </div>
        </div>

        {movementType === 'payment' && (
          <Select
            label="Metodo de pago"
            options={paymentMethodOptions}
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
        )}

        <Input
          type="date"
          label={movementType === 'payment' ? 'Fecha de pago' : 'Fecha de la nota'}
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
        />

        <Input
          label="Referencia"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Nro. de transferencia, cheque, etc."
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
            placeholder={movementType === 'payment' ? 'Notas del pago...' : 'Motivo de la nota...'}
          />
        </div>

        {movementType === 'payment' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={deductFromRegister}
              onChange={(e) => setDeductFromRegister(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Descontar de la caja del dia</span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          {confirmingPayment ? (
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-700">
                Registrar {SUPPLIER_MOVEMENT_LABELS[movementType].toLowerCase()} de{' '}
                <span className="font-bold text-green-700">{formatPrice(n(amount))}</span>?
              </p>
              <Button type="submit" loading={registerPayment.isPending}>
                Confirmar
              </Button>
              <button
                type="button"
                onClick={() => setConfirmingPayment(false)}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <>
              {pendingAmount > 0 && n(amount) > pendingAmount ? (
                <p className="text-sm text-red-600">El monto no puede superar la deuda pendiente</p>
              ) : null}
              <Button type="submit" disabled={n(amount) <= 0 || (pendingAmount > 0 && n(amount) > pendingAmount) || pendingAmount <= 0}>
                Registrar Pago
              </Button>
            </>
          )}
        </div>
      </form>

      {/* Payment history */}
      {payments && payments.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 uppercase mb-2">Historial de pagos</h4>
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metodo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ref.</th>
                  {isAdmin && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-3 py-2 text-sm text-gray-700">{formatDate(payment.payment_date)}</td>
                    <td className={`px-3 py-2 text-sm text-right font-medium ${
                      payment.movement_type === 'debit_note' ? 'text-red-600'
                        : payment.movement_type === 'credit_note' ? 'text-blue-600'
                        : 'text-green-700'
                    }`}>
                      {payment.movement_type === 'debit_note' ? '+' : '-'}{formatPrice(payment.amount)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {payment.movement_type && payment.movement_type !== 'payment'
                        ? SUPPLIER_MOVEMENT_LABELS[payment.movement_type]
                        : PAYMENT_METHOD_LABELS[payment.payment_method]
                          ? s(PAYMENT_METHOD_LABELS[payment.payment_method])
                          : s(payment.payment_method)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">{payment.reference ? s(payment.reference) : '-'}</td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-center">
                        {deletingPaymentId === payment.id ? (
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              onClick={async () => {
                                await deletePayment.mutateAsync(payment.id);
                                setDeletingPaymentId(null);
                              }}
                              className="text-xs text-red-600 font-medium hover:text-red-800"
                            >
                              Si
                            </button>
                            <span className="text-gray-400">|</span>
                            <button
                              onClick={() => setDeletingPaymentId(null)}
                              className="text-xs text-gray-500 font-medium hover:text-gray-700"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingPaymentId(payment.id)}
                            className="text-red-400 hover:text-red-600"
                            title="Eliminar pago"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface ProductSearchSelectProps {
  value: string;
  searchValue?: string;
  isOpen: boolean;
  results: Array<{ id: number; name: string }>;
  isSearching: boolean;
  onFocus: () => void;
  onSearch: (val: string) => void;
  onSelect: (product: { id: number; name: string }) => void;
}

function ProductSearchSelect({ value, searchValue, isOpen, results, isSearching, onFocus, onSearch, onSelect }: ProductSearchSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 350),
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? (searchValue ?? '') : value}
        onChange={(e) => onSearch(e.target.value)}
        onFocus={onFocus}
        placeholder="Buscar producto..."
        className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
      />
      {isOpen && (
        <div
          style={dropdownStyle}
          className="bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-y-auto"
        >
          {results
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700"
                onClick={() => onSelect(p)}
              >{p.name}</button>
            ))}
          {results.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-400">
              {isSearching ? 'Sin resultados' : 'Cargando...'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
